import { useEffect, useRef, useState } from "react";
import {
  useGetAppointmentsProgramsTherapistsMutation,
  useGetAppointmentsProgramsUnavailableTherapistsMutation,
} from "../services/api/utils.api";
import { IAppointmentsProgram } from "../services/types/utils.types";
import { IUserReport } from "../services/types/users.types";

interface UseTherapistOptionsParams {
  form: IAppointmentsProgram;
  user: IUserReport;
  adminCompanyId: number;
  adminGuid: string;
  showUnavailable: boolean;
  onResetTherapistGuid: () => void;
}

function useTherapistOptions({
  form,
  user,
  adminCompanyId,
  adminGuid,
  showUnavailable,
  onResetTherapistGuid,
}: UseTherapistOptionsParams) {
  const [enabled, setEnabled] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  
  // Use ref to store the callback to avoid it being in dependencies
  const onResetTherapistGuidRef = useRef(onResetTherapistGuid);
  
  // Update ref when callback changes
  useEffect(() => {
    onResetTherapistGuidRef.current = onResetTherapistGuid;
  }, [onResetTherapistGuid]);

  const [
    getAppointmentsProgramsTherapists,
    { isLoading: isGettingTherapists },
  ] = useGetAppointmentsProgramsTherapistsMutation();
  const [getAppointmentsProgramsUnavailableTherapists] =
    useGetAppointmentsProgramsUnavailableTherapistsMutation();

  useEffect(() => {
    onResetTherapistGuidRef.current();

    if (enabled) {
      setShowCheckboxes(true);

      if (showUnavailable) {
        getAppointmentsProgramsUnavailableTherapists({
          appointmentsDurationsId: form.appointmentsDurationsId,
          appointmentsSequenceId: form.appointmentsSequenceId,
          countOfAppointments: form.countOfAppointments,
          roleId: form.roleId,
          companyId: adminCompanyId,
          usersGuid: user.Users.Guid ?? "0",
          vetek: form.vetek,
          genderId: form.genderId,
          migzarId: form.migzarId,
          therapistsSpecializationsIds:
            Array.isArray(form.therapistsSpecializationsIds) &&
            form.therapistsSpecializationsIds.length === 0
              ? null
              : form.therapistsSpecializationsIds,
        });
      } else {
        getAppointmentsProgramsTherapists({
          appointmentsDurationsId: form.appointmentsDurationsId,
          appointmentsSequenceId: form.appointmentsSequenceId,
          countOfAppointments: form.countOfAppointments,
          roleId: form.roleId,
          companyId: adminCompanyId,
          usersGuid: user.Users.Guid ?? "0",
          vetek: form.vetek,
          genderId: form.genderId,
          migzarId: form.migzarId,
          therapistsSpecializationsIds:
            Array.isArray(form.therapistsSpecializationsIds) &&
            form.therapistsSpecializationsIds.length === 0
              ? null
              : form.therapistsSpecializationsIds,
        });
      }
    } else {
      setShowCheckboxes(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    form.appointmentsDurationsId,
    form.appointmentsSequenceId,
    form.countOfAppointments,
    form.roleId,
    user.Users.Guid,
    adminCompanyId,
    form.vetek,
    form.genderId,
    form.migzarId,
    form.therapistsSpecializationsIds,
    showUnavailable,
    getAppointmentsProgramsTherapists,
    getAppointmentsProgramsUnavailableTherapists,
  ]);

  return { enabled, setEnabled, showCheckboxes, isGettingTherapists };
}

export default useTherapistOptions;

